import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import Kilometrina from "../Kilometrina/KilometrinaMap";
import ObracunObresti from "../ObracunObresti/ObracunObresti";
import { apiFetch } from "../../api";
import "./Stroskovnik.css";

let lastStroskovnikTotal = 0;

export function getLastStroskovnikTotal() {
    return lastStroskovnikTotal;
}

const MAX_PDF_BYTES = 20 * 1024 * 1024;

const PRAZNA_STRANKA = {
    name: "",
    address: "",
    post: "",
    country: "",
    taxNo: "",
};

const ZACETNE_POSTAVKE = [
    { id: 1, name: "Obračun stroškov", tarifa: "", points: 0, qty: 1 },
    { id: 2, name: "Seznanitev s spisom in vpis v evidenco", tarifa: "", points: 0, qty: 1 },
    { id: 3, name: "Izračun zamudnih obresti", tarifa: "", points: 0, qty: 1 },
    { id: 4, name: "Poizvedba o dolžniku:", tarifa: "", points: 0, qty: 1 },
    { id: 6, name: "Poizvedba o lastništvu vozila:", tarifa: "", points: 0, qty: 1 },
    { id: 8, name: "Vložitev zahtev v registru", tarifa: "", points: 0, qty: 1 },
    { id: 9, name: "Nadomestilo za opravo vpisa", tarifa: "", points: 0, qty: 1 },
    { id: 10, name: "Kilometrina (izračun spodaj)", tarifa: "", points: 0, qty: 0 },
    { id: 12, name: "Rubež (izberi opcijo)", tarifa: "", points: 0, qty: 1 },
    { id: 13, name: "Materialni stroški", tarifa: "", points: 0, qty: 1 },
];

const RUBEZ_OPTIONS = [
    {
        value: "po vrednosti terjatve",
        label: "Rubež po vrednosti terjatve (tarifa št. 1)",
        tarifa: "1-1",
        points: null,
    },
    {
        value: "gotovina_50",
        label: "Rubež gotovine (tarifa št. 1)",
        tarifa: "1-2",
        points: null,
    },
    {
        value: "nepremicnina",
        label: "Rubež nepremičnine (tarifa št. 1)",
        tarifa: "1-3",
        points: null,
    },
];

const normalizirajStevilke = (value, max = 10) =>
    String(value || "").replace(/\D/g, "").slice(0, max);

const toNumberOrZero = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const round2 = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return Math.round((n + Number.EPSILON) * 100) / 100;
};

const filenameSafe = (value) =>
    String(value || "stroskovnik")
        .trim()
        .replace(/[^\p{L}\p{N}_-]+/gu, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 90) || "stroskovnik";

const formatEUR = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "0,00 €";

    return n.toLocaleString("sl-SI", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }) + " €";
};

const formatDatum = (value) => {
    if (!value) return "—";

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";

    return d.toLocaleDateString("sl-SI");
};

const partyFromAjpes = (ajpes, fallbackTax = "") => {
    if (!ajpes) {
        return {
            ...PRAZNA_STRANKA,
            taxNo: fallbackTax || "",
        };
    }

    return {
        name: ajpes.naziv ?? ajpes.Naziv ?? ajpes.name ?? ajpes.Name ?? "",
        address: ajpes.naslov ?? ajpes.Naslov ?? ajpes.address ?? ajpes.Address ?? "",
        post: ajpes.posta ?? ajpes.Posta ?? ajpes.post ?? ajpes.Post ?? "",
        country: ajpes.drzava ?? ajpes.Drzava ?? ajpes.country ?? ajpes.Country ?? "",
        taxNo:
            ajpes.davcnaStevilka ??
            ajpes.DavcnaStevilka ??
            ajpes.taxNo ??
            ajpes.TaxNo ??
            fallbackTax ??
            "",
    };
};

const partyFromSavedStran = (data, kind, fallbackTax = "") => {
    const pd = data?.podatkiStrani || {};
    const ajpes = kind === "upnik" ? pd.ajpesUpnik : pd.ajpesDolznik;

    const party = partyFromAjpes(
        ajpes,
        normalizirajStevilke(pd.davcnaAliMaticna || fallbackTax)
    );

    return {
        ...PRAZNA_STRANKA,
        ...party,
        taxNo: party.taxNo || pd.davcnaAliMaticna || fallbackTax || "",
    };
};

const mergeNonEmpty = (oldValue, nextValue) => {
    const copy = { ...oldValue };

    for (const [key, value] of Object.entries(nextValue || {})) {
        if (String(value || "").trim()) {
            copy[key] = value;
        }
    }

    return copy;
};

const preberiSporociloNapake = async (res, fallback) => {
    try {
        const json = await res.json();
        return json?.message || json?.title || fallback;
    } catch {
        const text = await res.text().catch(() => "");
        return text || fallback;
    }
};

export default function Stroskovnik({
    embedded = false,
    showBackButton = true,
    showActions = true,

    // NEW: used by NovPrimer to hide duplicated fields
    showPartyLookup = true,
    showCaseHeader = true,
    showPartyData = true,
    showPrintCaseData = true,

    // NEW: optional principal from NovPrimer
    initialPrincipal = null,

    initialValue = null,
    valueKey = "",
    initialCase = null,
    onChange,
}) {
    const navigate = useNavigate();

    const printRef = useRef(null);
    const printDocumentRef = useRef(null);

    const [fileNo, setFileNo] = useState("");
    const [courtCaseNo, setCourtCaseNo] = useState("");
    const [executorName, setExecutorName] = useState("");

    const [debtor, setDebtor] = useState(() => ({ ...PRAZNA_STRANKA }));
    const [upnik, setUpnik] = useState(() => ({ ...PRAZNA_STRANKA }));

    const [upnikLookupInput, setUpnikLookupInput] = useState("");
    const [debtorLookupInput, setDebtorLookupInput] = useState("");

    const [upnikLookupLoading, setUpnikLookupLoading] = useState(false);
    const [debtorLookupLoading, setDebtorLookupLoading] = useState(false);

    const [upnikLookupMsg, setUpnikLookupMsg] = useState("");
    const [debtorLookupMsg, setDebtorLookupMsg] = useState("");

    const [pointValue, setPointValue] = useState(0.48);
    const [porocilo, setPorocilo] = useState("");
    const [rubezOption, setRubezOption] = useState("po vrednosti terjatve");
    const [kilometrinaResult, setKilometrinaResult] = useState(null);
    const [obrestiResult, setObrestiResult] = useState(null);
    const [items, setItems] = useState(() => [...ZACETNE_POSTAVKE]);

    const [actionMsg, setActionMsg] = useState("");
    const [exportingPdf, setExportingPdf] = useState(false);

    const nextDynamicItemIdRef = useRef(1000);
    const appliedInitialKeyRef = useRef(null);

    const allocDynamicId = () => nextDynamicItemIdRef.current++;

    useEffect(() => {
        let alive = true;

        if (embedded) return undefined;

        (async () => {
            try {
                const res = await apiFetch("/api/auth/me");

                if (!res.ok) return;

                const data = await res.json();

                if (!alive) return;

                const fullName =
                    (data.fullName && data.fullName.trim()) ||
                    [data.firstName, data.lastName].filter(Boolean).join(" ").trim() ||
                    data.email ||
                    "";

                setExecutorName((old) => old || fullName);
            } catch {
                // ignore
            }
        })();

        return () => {
            alive = false;
        };
    }, [embedded]);

    useEffect(() => {
        if (!initialCase) return;

        if (initialCase.executorName) {
            setExecutorName(initialCase.executorName);
        }

        if (typeof initialCase.stevilkaSpisaIzvrsitelja === "string") {
            setFileNo(initialCase.stevilkaSpisaIzvrsitelja);
        }

        if (typeof initialCase.opravilnaStevilkaSodisca === "string") {
            setCourtCaseNo(initialCase.opravilnaStevilkaSodisca);
        }

        if (initialCase.upnik) {
            const party = partyFromAjpes(
                initialCase.upnik.ajpesUpnik,
                normalizirajStevilke(initialCase.upnik.davcnaAliMaticna)
            );

            setUpnik((old) => mergeNonEmpty(old, party));

            const tax = normalizirajStevilke(
                initialCase.upnik.davcnaAliMaticna || party.taxNo
            );

            if (tax) setUpnikLookupInput(tax);
        }

        if (initialCase.dolznik) {
            const party = partyFromAjpes(
                initialCase.dolznik.ajpesDolznik,
                normalizirajStevilke(initialCase.dolznik.davcnaAliMaticna)
            );

            setDebtor((old) => mergeNonEmpty(old, party));

            const tax = normalizirajStevilke(
                initialCase.dolznik.davcnaAliMaticna || party.taxNo
            );

            if (tax) setDebtorLookupInput(tax);
        }
    }, [
        initialCase?.executorName,
        initialCase?.stevilkaSpisaIzvrsitelja,
        initialCase?.opravilnaStevilkaSodisca,
        initialCase?.upnik?.davcnaAliMaticna,
        initialCase?.upnik?.ajpesUpnik,
        initialCase?.dolznik?.davcnaAliMaticna,
        initialCase?.dolznik?.ajpesDolznik,
    ]);

    useEffect(() => {
        const key = String(valueKey || "default");

        if (!initialValue || appliedInitialKeyRef.current === key) {
            return;
        }

        appliedInitialKeyRef.current = key;

        if (typeof initialValue.executorFileNo === "string") {
            setFileNo(initialValue.executorFileNo);
        }

        if (typeof initialValue.courtCaseNo === "string") {
            setCourtCaseNo(initialValue.courtCaseNo);
        }

        if (typeof initialValue.executorName === "string") {
            setExecutorName(initialValue.executorName);
        }

        if (initialValue.upnik) {
            const nextUpnik = { ...PRAZNA_STRANKA, ...initialValue.upnik };
            setUpnik(nextUpnik);

            const tax = normalizirajStevilke(nextUpnik.taxNo);
            if (tax) setUpnikLookupInput(tax);
        }

        if (initialValue.debtor) {
            const nextDebtor = { ...PRAZNA_STRANKA, ...initialValue.debtor };
            setDebtor(nextDebtor);

            const tax = normalizirajStevilke(nextDebtor.taxNo);
            if (tax) setDebtorLookupInput(tax);
        }

        if (Number.isFinite(Number(initialValue.pointValue))) {
            setPointValue(Number(initialValue.pointValue));
        }

        if (Array.isArray(initialValue.items)) {
            setItems(
                initialValue.items.map((item) => ({
                    id: Number(item.id) || 0,
                    name: item.name || "",
                    tarifa: item.tarifa || "",
                    points: toNumberOrZero(item.points),
                    qty: Number.isFinite(Number(item.qty)) ? Number(item.qty) : 1,
                    __custom: Boolean(item.__custom || item.custom),
                }))
            );
        }

        if (initialValue.kilometrinaResult) {
            setKilometrinaResult(initialValue.kilometrinaResult);
        }

        if (initialValue.obrestiResult) {
            setObrestiResult(initialValue.obrestiResult);
        }

        if (typeof initialValue.rubezOption === "string") {
            setRubezOption(initialValue.rubezOption);
        }

        if (typeof initialValue.porocilo === "string") {
            setPorocilo(initialValue.porocilo);
        }
    }, [initialValue, valueKey]);

    const pridobiPartyIzVirov = async (kind) => {
        const isUpnik = kind === "upnik";

        const input = isUpnik ? upnikLookupInput : debtorLookupInput;
        const setInput = isUpnik ? setUpnikLookupInput : setDebtorLookupInput;
        const setLoading = isUpnik ? setUpnikLookupLoading : setDebtorLookupLoading;
        const setMsg = isUpnik ? setUpnikLookupMsg : setDebtorLookupMsg;
        const setParty = isUpnik ? setUpnik : setDebtor;
        const loading = isUpnik ? upnikLookupLoading : debtorLookupLoading;

        if (loading) return;

        const label = isUpnik ? "upnika" : "dolžnika";
        const endpoint = isUpnik ? "upnik" : "dolznik";

        setMsg("");

        const stevke = normalizirajStevilke(input);

        if (!stevke) {
            setMsg(`Vpišite davčno ali matično številko ${label}.`);
            return;
        }

        if (stevke.length !== 8 && stevke.length !== 10) {
            setMsg("Vpišite davčno (8) ali matično (10) številk.");
            return;
        }

        setInput(stevke);
        setLoading(true);

        try {
            const params = new URLSearchParams();
            params.set("davcnaAliMaticna", stevke);

            const dbRes = await apiFetch(`/api/stran/${endpoint}?${params.toString()}`, {
                method: "GET",
            });

            if (dbRes.ok) {
                const data = await dbRes.json();
                const party = partyFromSavedStran(data, kind, stevke);

                setParty({
                    ...PRAZNA_STRANKA,
                    ...party,
                    taxNo: party.taxNo || stevke,
                });

                setMsg(
                    isUpnik
                        ? "Shranjeni podatki upnika so naloženi iz baze."
                        : "Shranjeni podatki dolžnika so naloženi iz baze."
                );

                return;
            }

            if (dbRes.status !== 404) {
                throw new Error(
                    await preberiSporociloNapake(
                        dbRes,
                        isUpnik
                            ? "Napaka pri pridobivanju upnika iz baze."
                            : "Napaka pri pridobivanju dolžnika iz baze."
                    )
                );
            }

            const ajpesRes = await apiFetch(`/api/ajpes/prs/${stevke}`, {
                method: "GET",
            });

            if (!ajpesRes.ok) {
                throw new Error(
                    await preberiSporociloNapake(
                        ajpesRes,
                        "AJPES poizvedba ni uspela."
                    )
                );
            }

            const ajpesData = await ajpesRes.json();
            const party = partyFromAjpes(ajpesData, stevke);

            setParty({
                ...PRAZNA_STRANKA,
                ...party,
                taxNo: party.taxNo || stevke,
            });

            setMsg(
                isUpnik
                    ? "Podatki upnika so pridobljeni iz AJPES."
                    : "Podatki dolžnika so pridobljeni iz AJPES."
            );
        } catch (e) {
            setMsg(
                e?.message ||
                (isUpnik
                    ? "Napaka pri poizvedbi upnika."
                    : "Napaka pri poizvedbi dolžnika.")
            );
        } finally {
            setLoading(false);
        }
    };

    const pridobiUpnikaZaStroskovnik = () => pridobiPartyIzVirov("upnik");
    const pridobiDolznikaZaStroskovnik = () => pridobiPartyIzVirov("dolznik");

    const debtorLabel = (party) => {
        const name = (party?.name || "").trim();

        if (name) return name;

        const taxNo = (party?.taxNo || "").trim();

        if (taxNo) return taxNo;

        return "";
    };

    const handleDebtorChange = (field) => (e) => {
        const value = e.target.value;
        setDebtor((prev) => ({ ...prev, [field]: value }));
    };

    const handleUpnikChange = (field) => (e) => {
        const value = e.target.value;
        setUpnik((prev) => ({ ...prev, [field]: value }));
    };

    const dodajPraznoPostavko = () => {
        const novaPostavka = {
            id: allocDynamicId(),
            name: "",
            tarifa: "",
            points: 0,
            qty: 1,
            __custom: true,
        };

        setItems((prev) => {
            const pos = prev.findIndex((x) => Number(x.id) === 13);

            if (pos >= 0) {
                return [
                    ...prev.slice(0, pos + 1),
                    novaPostavka,
                    ...prev.slice(pos + 1),
                ];
            }

            return [...prev, novaPostavka];
        });
    };

    const odstraniPostavko = (id) => {
        setItems((prev) => prev.filter((x) => x.id !== id));
    };

    const updateItem = (id, field, value) => {
        setItems((prev) =>
            prev.map((item) =>
                item.id === id
                    ? {
                        ...item,
                        [field]: value,
                    }
                    : item
            )
        );
    };

    const applyRubezOption = (value) => {
        setRubezOption(value);

        const option = RUBEZ_OPTIONS.find((x) => x.value === value);

        if (!option) return;

        setItems((prev) =>
            prev.map((item) => {
                if (Number(item.id) !== 12) return item;

                return {
                    ...item,
                    tarifa: option.tarifa ?? item.tarifa,
                    points: option.points != null ? option.points : item.points,
                    qty: 1,
                };
            })
        );
    };

    useEffect(() => {
        const label = debtorLabel(debtor);

        setItems((prev) =>
            prev.map((item) => {
                if (Number(item.id) === 4) {
                    return {
                        ...item,
                        name: `Poizvedba o dolžniku: ${label}`,
                    };
                }

                if (Number(item.id) === 6) {
                    return {
                        ...item,
                        name: `Poizvedba o lastništvu vozila: ${label}`,
                    };
                }

                return item;
            })
        );
    }, [debtor]);

    useEffect(() => {
        const maxId = items.reduce((max, item) => {
            const idNum = Number(item.id);
            return Number.isFinite(idNum) && idNum > max ? idNum : max;
        }, 0);

        if (maxId >= 1000 && nextDynamicItemIdRef.current <= maxId) {
            nextDynamicItemIdRef.current = maxId + 1;
        }
    }, [items]);

    const sum = items.reduce((acc, item) => {
        if (Number(item.id) === 10) {
            const kmCost = Number(kilometrinaResult?.costEUR);
            return acc + (Number.isFinite(kmCost) ? kmCost : 0);
        }

        const points = toNumberOrZero(item.points);
        const qty = toNumberOrZero(item.qty);
        const point = toNumberOrZero(pointValue);

        return acc + points * qty * point;
    }, 0);

    const ddv = sum * 0.22;
    const total = sum + ddv;

    const printableRows = useMemo(() => {
        return items.map((item) => {
            const isKm = Number(item.id) === 10;
            const kmCost = Number(kilometrinaResult?.costEUR);

            const line =
                isKm && Number.isFinite(kmCost)
                    ? kmCost
                    : toNumberOrZero(item.points) *
                    toNumberOrZero(item.qty) *
                    toNumberOrZero(pointValue);

            const rubezLabel =
                RUBEZ_OPTIONS.find((option) => option.value === rubezOption)?.label ||
                "Rubež";

            return {
                id: item.id,
                name: Number(item.id) === 12 ? rubezLabel : item.name || "—",
                tarifa: isKm ? "" : item.tarifa || "",
                points: isKm ? "" : toNumberOrZero(item.points),
                amount: round2(line),
                isKm,
            };
        });
    }, [items, kilometrinaResult, pointValue, rubezOption]);

    const snapshot = useMemo(() => {
        const safeItems = Array.isArray(items) ? items : [];

        return {
            executorFileNo: (fileNo || "").trim(),
            courtCaseNo: (courtCaseNo || "").trim(),
            executorName: executorName || "",

            upnik: upnik || { ...PRAZNA_STRANKA },
            debtor: debtor || { ...PRAZNA_STRANKA },

            pointValue: Number.isFinite(Number(pointValue)) ? Number(pointValue) : 0.48,

            items: safeItems.map((item) => ({
                id: Number(item.id) || 0,
                name: item.name || "",
                tarifa: item.tarifa || "",
                points: Number.isFinite(Number(item.points)) ? Number(item.points) : 0,
                qty: Number.isFinite(Number(item.qty)) ? Number(item.qty) : 1,
                __custom: Boolean(item.__custom),
            })),

            kilometrinaResult: kilometrinaResult
                ? {
                    distanceKm: toNumberOrZero(kilometrinaResult.distanceKm),
                    durationMin: toNumberOrZero(kilometrinaResult.durationMin),
                    rateEURPerKm: toNumberOrZero(kilometrinaResult.rateEURPerKm),
                    costEUR: round2(kilometrinaResult.costEUR),
                }
                : null,

            obrestiResult: obrestiResult || null,

            sum: round2(sum),
            ddv: round2(ddv),
            total: round2(total),

            rubezOption: rubezOption || "",
            porocilo: porocilo || "",
        };
    }, [
        fileNo,
        courtCaseNo,
        executorName,
        upnik,
        debtor,
        pointValue,
        items,
        kilometrinaResult,
        obrestiResult,
        sum,
        ddv,
        total,
        rubezOption,
        porocilo,
    ]);

    useEffect(() => {
        const value = Number(total);

        if (!Number.isFinite(value)) return;

        lastStroskovnikTotal = value;

        if (typeof window !== "undefined") {
            window.dispatchEvent(
                new CustomEvent("stroskovnik:total", {
                    detail: value,
                })
            );
        }
    }, [total]);

    useEffect(() => {
        if (typeof onChange === "function") {
            onChange(snapshot);
        }
    }, [snapshot, onChange]);

    const buildPrintHtml = (bodyHtml, title = "Stroškovnik") => `<!doctype html>
<html lang="sl">
<head>
    <meta charset="utf-8" />
    <title>${String(title || "Stroškovnik").replace(/[<>]/g, "")}</title>
    <style>
        @page {
            size: A4 portrait;
            margin: 10mm;
        }

        html,
        body {
            margin: 0;
            padding: 0;
            background: #ffffff;
            color: #000000;
            font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
            font-size: 13px;
            line-height: 1.35;
        }

        * {
            box-sizing: border-box;
        }

        .stroskovnik__printOnly,
        .stroskovnik__printFrameDoc {
            display: block !important;
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
            color: #000000 !important;
        }

        .stroskovnik__printHeader {
            padding-bottom: 10px;
            border-bottom: 2px solid #111827;
            margin-bottom: 16px;
            break-inside: avoid;
            page-break-inside: avoid;
        }

        .stroskovnik__printHeader h1 {
            margin: 0;
            font-size: 24px;
            color: #111827;
        }

        .stroskovnik__printHeader p {
            margin: 4px 0 0;
            font-size: 13px;
            color: #4b5563;
        }

        .stroskovnik__printSection {
            margin: 0 0 16px;
            padding: 0;
            break-inside: avoid;
            page-break-inside: avoid;
        }

        .stroskovnik__printSection h2 {
            margin: 0 0 8px;
            font-size: 16px;
            color: #111827;
            border-bottom: 1px solid #d1d5db;
            padding-bottom: 4px;
        }

        .stroskovnik__printSection--tarife {
            break-before: auto;
            page-break-before: auto;
        }

        .stroskovnik__printRestPage {
            break-before: page;
            page-break-before: always;
        }

        .stroskovnik__printSection--kilometrina,
        .stroskovnik__printSection--obresti,
        .stroskovnik__printSection--porocilo {
            margin-bottom: 7px;
        }

        .stroskovnik__printSection--kilometrina h2,
        .stroskovnik__printSection--obresti h2,
        .stroskovnik__printSection--porocilo h2 {
            margin-bottom: 5px;
            padding-bottom: 3px;
        }

        .stroskovnik__printInfoGrid {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 8px;
        }

        .stroskovnik__printInfoGrid > div {
            border: 1px solid #d1d5db;
            padding: 8px;
            background: #ffffff;
        }

        .stroskovnik__printInfoGrid strong {
            display: block;
            font-size: 11px;
            text-transform: uppercase;
            color: #4b5563;
            margin-bottom: 3px;
        }

        .stroskovnik__printInfoGrid span {
            display: block;
            font-size: 13px;
            color: #111827;
            word-break: break-word;
        }

        .stroskovnik__printPartyGrid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 10px;
        }

        .stroskovnik__printParty {
            border: 1px solid #d1d5db;
            padding: 10px;
            background: #ffffff;
        }

        .stroskovnik__printParty h3 {
            margin: 0 0 6px;
            font-size: 14px;
            color: #111827;
        }

        .stroskovnik__printParty p {
            margin: 3px 0;
            font-size: 12px;
            color: #111827;
            word-break: break-word;
        }

        .stroskovnik__printTable {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
            font-size: 12px;
            background: #ffffff;
            color: #000000;
        }

        .stroskovnik__printTable thead {
            display: table-header-group;
        }

        .stroskovnik__printTable th,
        .stroskovnik__printTable td {
            border: 1px solid #cbd5e1;
            padding: 6px 7px;
            vertical-align: top;
            color: #000000;
            word-break: break-word;
        }

        .stroskovnik__printTable th {
            background: #eeeeee;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.04em;
        }

        .stroskovnik__printTable th:nth-child(1),
        .stroskovnik__printTable td:nth-child(1) {
            width: 55%;
        }

        .stroskovnik__printTable th:nth-child(2),
        .stroskovnik__printTable td:nth-child(2) {
            width: 15%;
            text-align: center;
        }

        .stroskovnik__printTable th:nth-child(3),
        .stroskovnik__printTable td:nth-child(3) {
            width: 12%;
            text-align: center;
        }

        .stroskovnik__printTable th:nth-child(4),
        .stroskovnik__printTable td:nth-child(4) {
            width: 18%;
            text-align: right;
        }

        .stroskovnik__printTable tr {
            break-inside: avoid;
            page-break-inside: avoid;
        }

        .stroskovnik__printSum {
            margin-top: 10px;
            display: grid;
            gap: 4px;
            justify-content: end;
            text-align: right;
            font-size: 13px;
            color: #111827;
        }

        .stroskovnik__printMuted {
            color: #6b7280;
            font-size: 13px;
            margin: 0;
        }

        .stroskovnik__printReport {
            white-space: pre-wrap;
            font-size: 13px;
            line-height: 1.45;
            color: #111827;
            margin: 0;
            word-break: break-word;
        }
    </style>
</head>
<body>
    ${bodyHtml}
</body>
</html>`;

    const obNatisni = () => {
        setActionMsg("");

        if (!printDocumentRef.current) {
            setActionMsg("Ni vsebine za tiskanje.");
            return;
        }

        const oldFrame = document.getElementById("stroskovnik-print-frame");
        if (oldFrame) {
            oldFrame.remove();
        }

        const clone = printDocumentRef.current.cloneNode(true);
        clone.classList.add("stroskovnik__printFrameDoc");
        clone.style.display = "block";

        const iframe = document.createElement("iframe");
        iframe.id = "stroskovnik-print-frame";
        iframe.title = "Tiskanje stroškovnika";
        iframe.setAttribute("aria-hidden", "true");

        iframe.style.position = "fixed";
        iframe.style.left = "-10000px";
        iframe.style.top = "0";
        iframe.style.width = "1px";
        iframe.style.height = "1px";
        iframe.style.border = "0";
        iframe.style.opacity = "0";
        iframe.style.pointerEvents = "none";

        document.body.appendChild(iframe);

        const frameWindow = iframe.contentWindow;
        const frameDocument = frameWindow?.document;

        if (!frameWindow || !frameDocument) {
            iframe.remove();
            setActionMsg("Tiskanje ni mogoče zagnati.");
            return;
        }

        const imeDokumenta = fileNo || courtCaseNo || "Stroškovnik";

        frameDocument.open();
        frameDocument.write(buildPrintHtml(clone.outerHTML, imeDokumenta));
        frameDocument.close();

        const cleanup = () => {
            frameWindow.removeEventListener("afterprint", cleanup);

            setTimeout(() => {
                const currentFrame = document.getElementById("stroskovnik-print-frame");
                if (currentFrame) {
                    currentFrame.remove();
                }
            }, 300);
        };

        frameWindow.addEventListener("afterprint", cleanup);

        setTimeout(() => {
            try {
                frameWindow.focus();
                frameWindow.print();
            } catch {
                iframe.remove();
                setActionMsg("Napaka pri tiskanju.");
            }
        }, 250);
    };

    const ustvariPdfBlob = async ({ scale, quality }) => {
        if (!printDocumentRef.current) {
            throw new Error("Ni vsebine za izvoz PDF.");
        }

        const host = document.createElement("div");
        host.className = "stroskovnik__pdfHost";
        host.style.position = "fixed";
        host.style.left = "-10000px";
        host.style.top = "0";
        host.style.width = "794px";
        host.style.background = "#ffffff";
        host.style.zIndex = "-1";

        const clone = printDocumentRef.current.cloneNode(true);
        clone.classList.add("stroskovnik__pdfDoc");
        clone.style.display = "block";
        clone.style.width = "794px";
        clone.style.maxWidth = "794px";
        clone.style.margin = "0";
        clone.style.background = "#ffffff";

        host.appendChild(clone);
        document.body.appendChild(host);

        try {
            await new Promise((resolve) => requestAnimationFrame(resolve));
            await new Promise((resolve) => requestAnimationFrame(resolve));

            const pdf = new jsPDF("p", "mm", "a4", true);

            const pageWidth = 210;
            const pageHeight = 297;
            const margin = 10;
            const usableWidth = pageWidth - margin * 2;
            const usableHeight = pageHeight - margin * 2;
            const blockGap = 2;

            let currentY = margin;
            let hasContent = false;

            const blocks = Array.from(clone.children).filter((el) => {
                return String(el.textContent || "").trim().length > 0;
            });

            const addPageIfNeeded = (heightMm) => {
                if (!hasContent) return;

                if (currentY + heightMm > pageHeight - margin) {
                    pdf.addPage();
                    currentY = margin;
                    hasContent = false;
                }
            };

            const addCanvas = (canvas) => {
                if (!canvas || !canvas.width || !canvas.height) return;

                const pxPerMm = canvas.width / usableWidth;
                const fullHeightMm = canvas.height / pxPerMm;

                if (fullHeightMm <= usableHeight) {
                    addPageIfNeeded(fullHeightMm);

                    const imgData = canvas.toDataURL("image/jpeg", quality);

                    pdf.addImage(
                        imgData,
                        "JPEG",
                        margin,
                        currentY,
                        usableWidth,
                        fullHeightMm,
                        undefined,
                        "FAST"
                    );

                    currentY += fullHeightMm + blockGap;
                    hasContent = true;
                    return;
                }

                let sourceY = 0;
                const maxSliceHeightPx = Math.floor(usableHeight * pxPerMm);

                while (sourceY < canvas.height) {
                    if (hasContent) {
                        pdf.addPage();
                        currentY = margin;
                        hasContent = false;
                    }

                    const sliceHeightPx = Math.min(
                        maxSliceHeightPx,
                        canvas.height - sourceY
                    );

                    const sliceCanvas = document.createElement("canvas");
                    sliceCanvas.width = canvas.width;
                    sliceCanvas.height = sliceHeightPx;

                    const ctx = sliceCanvas.getContext("2d");

                    ctx.drawImage(
                        canvas,
                        0,
                        sourceY,
                        canvas.width,
                        sliceHeightPx,
                        0,
                        0,
                        canvas.width,
                        sliceHeightPx
                    );

                    const sliceHeightMm = sliceHeightPx / pxPerMm;
                    const imgData = sliceCanvas.toDataURL("image/jpeg", quality);

                    pdf.addImage(
                        imgData,
                        "JPEG",
                        margin,
                        currentY,
                        usableWidth,
                        sliceHeightMm,
                        undefined,
                        "FAST"
                    );

                    sourceY += sliceHeightPx;
                    currentY = margin + sliceHeightMm + blockGap;
                    hasContent = true;
                }
            };

            for (const block of blocks) {
                const forceNewPageBefore =
                    block.classList.contains("stroskovnik__printRestPage");

                if (forceNewPageBefore && hasContent) {
                    pdf.addPage();
                    currentY = margin;
                    hasContent = false;
                }

                const canvas = await html2canvas(block, {
                    scale,
                    useCORS: true,
                    backgroundColor: "#ffffff",
                    logging: false,
                    removeContainer: true,
                    windowWidth: 794,
                });

                addCanvas(canvas);
            }

            const blob = pdf.output("blob");

            return {
                blob,
                size: blob.size,
            };
        } finally {
            document.body.removeChild(host);
        }
    };

    const shraniBlob = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");

        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();

        setTimeout(() => {
            URL.revokeObjectURL(url);
        }, 1000);
    };

    const obIzvoziPdf = async () => {
        if (!printDocumentRef.current || exportingPdf) return;

        setActionMsg("");
        setExportingPdf(true);

        try {
            const nastavitve = [
                { scale: 2, quality: 0.92 },
                { scale: 1.6, quality: 0.86 },
                { scale: 1.25, quality: 0.78 },
                { scale: 1, quality: 0.68 },
                { scale: 0.85, quality: 0.58 },
                { scale: 0.7, quality: 0.5 },
            ];

            let najboljsi = null;

            for (const n of nastavitve) {
                const rezultat = await ustvariPdfBlob(n);
                najboljsi = rezultat;

                if (rezultat.size <= MAX_PDF_BYTES) {
                    break;
                }
            }

            if (!najboljsi || najboljsi.size > MAX_PDF_BYTES) {
                setActionMsg(
                    "PDF je tudi po stiskanju večji od 20 MB. Skrajšajte vsebino ali odstranite del podatkov."
                );
                return;
            }

            const ime = filenameSafe(fileNo || courtCaseNo || "stroskovnik");
            shraniBlob(najboljsi.blob, `${ime}.pdf`);

            setActionMsg(
                `PDF je izvožen in stisnjen pod 20 MB (${(najboljsi.size / 1024 / 1024).toFixed(2)} MB).`
            );
        } catch (e) {
            setActionMsg(e?.message || "Napaka pri izvozu PDF.");
        } finally {
            setExportingPdf(false);
        }
    };

    const blankCell = (
        <span aria-hidden="true" style={{ display: "inline-block", minWidth: 10 }}>
            &nbsp;
        </span>
    );

    return (
        <div className={embedded ? "stroskovnik-tool" : "stroskovnik-glayout"}>
            <main className="stroskovnik__page stroskovnik">
                <section className="stroskovnik__content">
                    <div ref={printRef} className="stroskovnik__card">
                        {!embedded && (
                            <div className="stroskovnik__cardHeader stroskovnik__screenOnly">
                                <div className="stroskovnik__cardHeaderBrand">
                                    <div className="stroskovnik__cardHeaderMeta">
                                        <h1 className="stroskovnik__cardHeaderTitle">Stroškovnik</h1>
                                        <p className="stroskovnik__cardHeaderSubtitle">
                                            Izračun in pregled stroškov.
                                        </p>
                                    </div>
                                </div>

                                {showBackButton && (
                                    <button
                                        type="button"
                                        className="btn-nazaj stroskovnik__noPrint"
                                        onClick={() => navigate("/profil", { replace: true })}
                                        aria-label="Nazaj na profil"
                                        title="Nazaj"
                                    >
                                        <span className="btn-nazaj__icon" aria-hidden="true">
                                            ←
                                        </span>
                                    </button>
                                )}
                            </div>
                        )}

                        {(showCaseHeader || showPartyData) && (
                            <section className="stroskovnik__header stroskovnik__screenOnly">
                                {showCaseHeader && (
                                    <div className="stroskovnik__headerTop">
                                        <div className="stroskovnik__caseBlock">
                                            <label className="stroskovnik__field stroskovnik__field--top">
                                                <span className="stroskovnik__label globalniizv-label-icon-wrap">
                                                    Izvršitelj
                                                </span>
                                                <input
                                                    className="stroskovnik__input stroskovnik__input--top stroskovnik__input--executor"
                                                    value={executorName}
                                                    onChange={(e) => setExecutorName(e.target.value)}
                                                />
                                            </label>

                                            <label className="stroskovnik__field stroskovnik__field--top">
                                                <span className="stroskovnik__label">Št. spisa izvršitelja</span>
                                                <input
                                                    className="stroskovnik__input stroskovnik__input--top"
                                                    value={fileNo}
                                                    onChange={(e) => setFileNo(e.target.value)}
                                                />
                                            </label>

                                            <label className="stroskovnik__field stroskovnik__field--top">
                                                <span className="stroskovnik__label">Opr. št. sodišča</span>
                                                <input
                                                    className="stroskovnik__input stroskovnik__input--top"
                                                    value={courtCaseNo}
                                                    onChange={(e) => setCourtCaseNo(e.target.value)}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                )}

                                {showPartyData && (
                                    <div className="stroskovnik__partyArea">
                                        <div className="stroskovnik__debtorBlock">
                                            <div className="stroskovnik__debtorHeader">
                                                <h2 className="stroskovnik__sectionSubtitle">Podatki o upniku</h2>
                                            </div>

                                            <div className="stroskovnik__partyCard">
                                                <div className="stroskovnik__partyTitle">Upnik</div>

                                                {showPartyLookup && (
                                                    <div className="stroskovnik__lookup stroskovnik__noPrint">
                                                        <label className="stroskovnik__field stroskovnik__field--wide">
                                                            <span className="stroskovnik__label">
                                                                Matična ali davčna številka upnika (24995975)
                                                            </span>
                                                            <input
                                                                className="stroskovnik__input"
                                                                value={upnikLookupInput}
                                                                onChange={(e) => {
                                                                    setUpnikLookupInput(normalizirajStevilke(e.target.value));
                                                                    setUpnikLookupMsg("");
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter") {
                                                                        e.preventDefault();
                                                                        pridobiUpnikaZaStroskovnik();
                                                                    }
                                                                }}
                                                                inputMode="numeric"
                                                            />
                                                        </label>

                                                        <button
                                                            type="button"
                                                            className="stroskovnik__btn stroskovnik__lookupBtn"
                                                            onClick={pridobiUpnikaZaStroskovnik}
                                                            disabled={upnikLookupLoading}
                                                        >
                                                            {upnikLookupLoading ? "Iskanje…" : "Pridobi podatke"}
                                                        </button>
                                                    </div>
                                                )}

                                                {showPartyLookup && upnikLookupMsg && (
                                                    <div className="stroskovnik__lookupMsg stroskovnik__noPrint">
                                                        {upnikLookupMsg}
                                                    </div>
                                                )}

                                                <label className="stroskovnik__field stroskovnik__field--wide">
                                                    <span className="stroskovnik__label">Ime / firma</span>
                                                    <input
                                                        className="stroskovnik__input"
                                                        value={upnik.name}
                                                        onChange={handleUpnikChange("name")}
                                                    />
                                                </label>

                                                <label className="stroskovnik__field stroskovnik__field--wide">
                                                    <span className="stroskovnik__label">Naslov</span>
                                                    <input
                                                        className="stroskovnik__input"
                                                        value={upnik.address}
                                                        onChange={handleUpnikChange("address")}
                                                    />
                                                </label>

                                                <div className="stroskovnik__partyGrid">
                                                    <label className="stroskovnik__field">
                                                        <span className="stroskovnik__label">Pošta</span>
                                                        <input
                                                            className="stroskovnik__input"
                                                            value={upnik.post}
                                                            onChange={handleUpnikChange("post")}
                                                        />
                                                    </label>

                                                    <label className="stroskovnik__field">
                                                        <span className="stroskovnik__label">Država</span>
                                                        <input
                                                            className="stroskovnik__input"
                                                            value={upnik.country}
                                                            onChange={handleUpnikChange("country")}
                                                        />
                                                    </label>

                                                    <label className="stroskovnik__field">
                                                        <span className="stroskovnik__label">Davčna št.</span>
                                                        <input
                                                            className="stroskovnik__input"
                                                            value={upnik.taxNo}
                                                            onChange={handleUpnikChange("taxNo")}
                                                        />
                                                    </label>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="stroskovnik__debtorBlock">
                                            <div className="stroskovnik__debtorHeader">
                                                <h2 className="stroskovnik__sectionSubtitle">Podatki o dolžniku</h2>
                                            </div>

                                            <div className="stroskovnik__partyCard">
                                                <div className="stroskovnik__partyTitle">Dolžnik</div>

                                                {showPartyLookup && (
                                                    <div className="stroskovnik__lookup stroskovnik__noPrint">
                                                        <label className="stroskovnik__field stroskovnik__field--wide">
                                                            <span className="stroskovnik__label">
                                                                Matična ali davčna številka dolžnika (24995975)
                                                            </span>
                                                            <input
                                                                className="stroskovnik__input"
                                                                value={debtorLookupInput}
                                                                onChange={(e) => {
                                                                    setDebtorLookupInput(normalizirajStevilke(e.target.value));
                                                                    setDebtorLookupMsg("");
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter") {
                                                                        e.preventDefault();
                                                                        pridobiDolznikaZaStroskovnik();
                                                                    }
                                                                }}
                                                                inputMode="numeric"
                                                            />
                                                        </label>

                                                        <button
                                                            type="button"
                                                            className="stroskovnik__btn stroskovnik__lookupBtn"
                                                            onClick={pridobiDolznikaZaStroskovnik}
                                                            disabled={debtorLookupLoading}
                                                        >
                                                            {debtorLookupLoading ? "Iskanje…" : "Pridobi podatke"}
                                                        </button>
                                                    </div>
                                                )}

                                                {showPartyLookup && debtorLookupMsg && (
                                                    <div className="stroskovnik__lookupMsg stroskovnik__noPrint">
                                                        {debtorLookupMsg}
                                                    </div>
                                                )}

                                                <label className="stroskovnik__field stroskovnik__field--wide">
                                                    <span className="stroskovnik__label">Ime / firma</span>
                                                    <input
                                                        className="stroskovnik__input"
                                                        value={debtor.name}
                                                        onChange={handleDebtorChange("name")}
                                                    />
                                                </label>

                                                <label className="stroskovnik__field stroskovnik__field--wide">
                                                    <span className="stroskovnik__label">Naslov</span>
                                                    <input
                                                        className="stroskovnik__input"
                                                        value={debtor.address}
                                                        onChange={handleDebtorChange("address")}
                                                    />
                                                </label>

                                                <div className="stroskovnik__partyGrid">
                                                    <label className="stroskovnik__field">
                                                        <span className="stroskovnik__label">Pošta</span>
                                                        <input
                                                            className="stroskovnik__input"
                                                            value={debtor.post}
                                                            onChange={handleDebtorChange("post")}
                                                        />
                                                    </label>

                                                    <label className="stroskovnik__field">
                                                        <span className="stroskovnik__label">Država</span>
                                                        <input
                                                            className="stroskovnik__input"
                                                            value={debtor.country}
                                                            onChange={handleDebtorChange("country")}
                                                        />
                                                    </label>

                                                    <label className="stroskovnik__field">
                                                        <span className="stroskovnik__label">Davčna št.</span>
                                                        <input
                                                            className="stroskovnik__input"
                                                            value={debtor.taxNo}
                                                            onChange={handleDebtorChange("taxNo")}
                                                        />
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </section>
                        )}

                        <section className="stroskovnik__tarife stroskovnik__screenOnly">
                            <h2 className="stroskovnik__sectionTitle">Stroškovnik</h2>

                            <div className="stroskovnik__točkeWrap">
                                <label className="stroskovnik__field">
                                    <span className="stroskovnik__label">Vrednost točke (EUR)</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="stroskovnik__input"
                                        value={pointValue}
                                        onChange={(e) => setPointValue(Number(e.target.value))}
                                    />
                                </label>
                            </div>

                            <div className="stroskovnik__tableWrap">
                                <table className="stroskovnik__table">
                                    <thead>
                                        <tr>
                                            <th>Postavka</th>
                                            <th>Tarifa</th>
                                            <th>Točke</th>
                                            <th>Znesek</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {items.map((item) => {
                                            const isKm = Number(item.id) === 10;
                                            const kmCost = Number(kilometrinaResult?.costEUR);

                                            const line =
                                                isKm && Number.isFinite(kmCost)
                                                    ? kmCost
                                                    : toNumberOrZero(item.points) *
                                                    toNumberOrZero(item.qty) *
                                                    toNumberOrZero(pointValue);

                                            return (
                                                <React.Fragment key={item.id}>
                                                    <tr>
                                                        <td>
                                                            {Number(item.id) === 12 ? (
                                                                <>
                                                                    <select
                                                                        className="stroskovnik__select"
                                                                        value={rubezOption}
                                                                        onChange={(e) => applyRubezOption(e.target.value)}
                                                                    >
                                                                        {RUBEZ_OPTIONS.map((option) => (
                                                                            <option key={option.value} value={option.value}>
                                                                                {option.label}
                                                                            </option>
                                                                        ))}
                                                                    </select>

                                                                    <div className="stroskovnik__sourceHint">
                                                                        Vir:{" "}
                                                                        <a
                                                                            href="https://pisrs.si/pregledPredpisa?id=PRAV4692"
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                        >
                                                                            PRAV4692 - Tarifa izvršiteljev
                                                                        </a>
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {item.__custom ? (
                                                                        <div className="stroskovnik__customRowCell">
                                                                            <input
                                                                                className="stroskovnik__customRowInput"
                                                                                value={item.name}
                                                                                onChange={(e) =>
                                                                                    updateItem(item.id, "name", e.target.value)
                                                                                }
                                                                                placeholder="Vpiši naziv postavke"
                                                                            />

                                                                            <button
                                                                                type="button"
                                                                                className="stroskovnik__customRowRemoveBtn stroskovnik__noPrint"
                                                                                onClick={() => odstraniPostavko(item.id)}
                                                                                title="Odstrani postavko"
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        </div>
                                                                    ) : (
                                                                        item.name
                                                                    )}

                                                                    {isKm && kilometrinaResult && (
                                                                        <div className="stroskovnik__kmMeta">
                                                                            {toNumberOrZero(kilometrinaResult.distanceKm).toFixed(1)} km ×{" "}
                                                                            {toNumberOrZero(kilometrinaResult.rateEURPerKm).toFixed(2)} €/km
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </td>

                                                        <td style={{ textAlign: "center" }}>
                                                            {isKm ? (
                                                                blankCell
                                                            ) : (
                                                                <input
                                                                    className="stroskovnik__inputSmall"
                                                                    value={item.tarifa ?? ""}
                                                                    onChange={(e) =>
                                                                        updateItem(item.id, "tarifa", e.target.value)
                                                                    }
                                                                    placeholder="16-7"
                                                                />
                                                            )}
                                                        </td>

                                                        <td style={{ textAlign: "center" }}>
                                                            {isKm ? (
                                                                blankCell
                                                            ) : (
                                                                <input
                                                                    type="number"
                                                                    step="1"
                                                                    min="0"
                                                                    className="stroskovnik__inputSmall"
                                                                    value={
                                                                        Number.isFinite(Number(item.points))
                                                                            ? item.points
                                                                            : 0
                                                                    }
                                                                    onChange={(e) =>
                                                                        updateItem(item.id, "points", Number(e.target.value))
                                                                    }
                                                                />
                                                            )}
                                                        </td>

                                                        <td>
                                                            <div>{toNumberOrZero(line).toFixed(2)} €</div>
                                                        </td>
                                                    </tr>

                                                    {Number(item.id) === 13 && (
                                                        <tr className="stroskovnik__addRowLine stroskovnik__noPrint">
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    className="stroskovnik__addRowBtn stroskovnik__addRowBtn--inline"
                                                                    onClick={dodajPraznoPostavko}
                                                                    title="Dodaj prazno postavko"
                                                                >
                                                                    <span className="stroskovnik__addRowBtnPlus">+</span>
                                                                    <span>Dodaj postavko</span>
                                                                </button>
                                                            </td>
                                                            <td>{blankCell}</td>
                                                            <td>{blankCell}</td>
                                                            <td>{blankCell}</td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="stroskovnik__reportBlock">
                                <label className="stroskovnik__field stroskovnik__field--wide">
                                    <span className="stroskovnik__label">Obrazložitev stroškovnika</span>
                                    <textarea
                                        className="stroskovnik__reportInput"
                                        value={porocilo}
                                        onChange={(e) => setPorocilo(e.target.value)}
                                        rows={5}
                                        placeholder="Vpišite poročilo..."
                                    />
                                </label>
                            </div>

                            <div className="stroskovnik__sum">
                                <div>
                                    SKUPAJ: <strong>{sum.toFixed(2)} €</strong>
                                </div>
                                <div>
                                    DDV 22%: <strong>{ddv.toFixed(2)} €</strong>
                                </div>
                                <div className="stroskovnik__sumTotal">
                                    SKUPAJ Z DDV: <strong>{total.toFixed(2)} €</strong>
                                </div>
                            </div>
                        </section>

                        <section className="stroskovnik__section stroskovnik__section--kilometrina stroskovnik__noPrint stroskovnik__screenOnly">
                            <h2 className="stroskovnik__sectionTitle">Kilometrina</h2>
                            <Kilometrina onCalculated={setKilometrinaResult} />
                        </section>

                        <section className="stroskovnik__section stroskovnik__noPrint stroskovnik__screenOnly">
                            <h2 className="stroskovnik__sectionTitle">Zakonske zamudne obresti</h2>
                            <ObracunObresti
                                onCalculated={setObrestiResult}
                                initialPrincipal={initialPrincipal}
                            />
                        </section>

                        {showActions && !embedded && (
                            <div className="stroskovnik__actions stroskovnik__noPrint">
                                {actionMsg && (
                                    <span className="stroskovnik__saveMsg">
                                        {actionMsg}
                                    </span>
                                )}

                                <button
                                    type="button"
                                    className="stroskovnik__btn"
                                    onClick={obNatisni}
                                >
                                    Natisni
                                </button>

                                <button
                                    type="button"
                                    className="stroskovnik__btn"
                                    onClick={obIzvoziPdf}
                                    disabled={exportingPdf}
                                >
                                    {exportingPdf ? "Izvažam PDF…" : "Izvozi PDF"}
                                </button>
                            </div>
                        )}

                        <div ref={printDocumentRef} className="stroskovnik__printOnly">
                            <div className="stroskovnik__printHeader">
                                <h1>Stroškovnik</h1>
                                <p>Izračun in pregled stroškov</p>
                            </div>

                            {showPrintCaseData && (
                                <>
                                    <section className="stroskovnik__printSection stroskovnik__printSection--caseData">
                                        <h2>Osnovni podatki</h2>

                                        <div className="stroskovnik__printInfoGrid">
                                            <div>
                                                <strong>Izvršitelj</strong>
                                                <span>{executorName || "—"}</span>
                                            </div>

                                            <div>
                                                <strong>Št. spisa izvršitelja</strong>
                                                <span>{fileNo || "—"}</span>
                                            </div>

                                            <div>
                                                <strong>Opr. št. sodišča</strong>
                                                <span>{courtCaseNo || "—"}</span>
                                            </div>
                                        </div>
                                    </section>

                                    <section className="stroskovnik__printSection stroskovnik__printSection--caseData">
                                        <h2>Stranke</h2>

                                        <div className="stroskovnik__printPartyGrid">
                                            <div className="stroskovnik__printParty">
                                                <h3>Upnik</h3>
                                                <p><strong>Ime / firma:</strong> {upnik.name || "—"}</p>
                                                <p><strong>Naslov:</strong> {upnik.address || "—"}</p>
                                                <p><strong>Pošta:</strong> {upnik.post || "—"}</p>
                                                <p><strong>Država:</strong> {upnik.country || "—"}</p>
                                                <p><strong>Davčna št.:</strong> {upnik.taxNo || "—"}</p>
                                            </div>

                                            <div className="stroskovnik__printParty">
                                                <h3>Dolžnik</h3>
                                                <p><strong>Ime / firma:</strong> {debtor.name || "—"}</p>
                                                <p><strong>Naslov:</strong> {debtor.address || "—"}</p>
                                                <p><strong>Pošta:</strong> {debtor.post || "—"}</p>
                                                <p><strong>Država:</strong> {debtor.country || "—"}</p>
                                                <p><strong>Davčna št.:</strong> {debtor.taxNo || "—"}</p>
                                            </div>
                                        </div>
                                    </section>
                                </>
                            )}

                            <section className="stroskovnik__printSection stroskovnik__printSection--tarife">
                                <h2>Stroškovnik</h2>

                                <table className="stroskovnik__printTable">
                                    <thead>
                                        <tr>
                                            <th>Postavka</th>
                                            <th>Tarifa</th>
                                            <th>Točke</th>
                                            <th>Znesek</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {printableRows.map((row) => (
                                            <tr key={row.id}>
                                                <td>{row.name}</td>
                                                <td>{row.tarifa || "—"}</td>
                                                <td>{row.points === "" ? "—" : row.points}</td>
                                                <td>{formatEUR(row.amount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                <div className="stroskovnik__printSum">
                                    <div>Skupaj brez DDV: <strong>{formatEUR(sum)}</strong></div>
                                    <div>DDV 22%: <strong>{formatEUR(ddv)}</strong></div>
                                    <div>Skupaj z DDV: <strong>{formatEUR(total)}</strong></div>
                                </div>
                            </section>

                            <div className="stroskovnik__printRestPage">
                                {kilometrinaResult && (
                                    <section className="stroskovnik__printSection stroskovnik__printSection--kilometrina">
                                        <h2>Kilometrina</h2>

                                        <div className="stroskovnik__printInfoGrid">
                                            <div>
                                                <strong>Razdalja</strong>
                                                <span>{toNumberOrZero(kilometrinaResult.distanceKm).toFixed(1)} km</span>
                                            </div>

                                            <div>
                                                <strong>Vrednost/km</strong>
                                                <span>{formatEUR(kilometrinaResult.rateEURPerKm)}</span>
                                            </div>

                                            <div>
                                                <strong>Znesek</strong>
                                                <span>{formatEUR(kilometrinaResult.costEUR)}</span>
                                            </div>
                                        </div>
                                    </section>
                                )}

                                <section className="stroskovnik__printSection stroskovnik__printSection--obresti">
                                    <h2>Obračun obresti</h2>

                                    {obrestiResult ? (
                                        <div className="stroskovnik__printInfoGrid">
                                            <div>
                                                <strong>Obdobje</strong>
                                                <span>
                                                    {formatDatum(obrestiResult.startDate)} – {formatDatum(obrestiResult.endDate)}
                                                </span>
                                            </div>

                                            <div>
                                                <strong>Število dni</strong>
                                                <span>{obrestiResult.days ?? "—"}</span>
                                            </div>

                                            <div>
                                                <strong>Glavnica</strong>
                                                <span>{formatEUR(obrestiResult.principalSum)}</span>
                                            </div>

                                            <div>
                                                <strong>Obresti</strong>
                                                <span>{formatEUR(obrestiResult.interest)}</span>
                                            </div>

                                            <div>
                                                <strong>Skupaj</strong>
                                                <span>{formatEUR(obrestiResult.total)}</span>
                                            </div>

                                            <div>
                                                <strong>STOP datum</strong>
                                                <span>{obrestiResult.interestStopDate ? formatDatum(obrestiResult.interestStopDate) : "—"}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="stroskovnik__printMuted">
                                            Obračun obresti še ni izračunan.
                                        </p>
                                    )}
                                </section>

                                {porocilo.trim() && (
                                    <section className="stroskovnik__printSection stroskovnik__printSection--porocilo">
                                        <h2>Obrazložitev stroškovnika</h2>
                                        <p className="stroskovnik__printReport">{porocilo}</p>
                                    </section>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
}